/**
 * Actualizaciones de Rumbo (web/PWA).
 *
 * Cómo llega una versión nueva: `browser-preload.js` registra el service
 * worker de vite-plugin-pwa (`registerSW`). Cuando el navegador descarga un
 * worker nuevo y queda «en espera», llama a `onNeedRefresh`, que resuelve
 * `global.Actual.waitForUpdateReadyForDownload()` y pone a `true`
 * `global.Actual.isUpdateReadyForDownload()`. `global.Actual.applyAppUpdate()`
 * activa ese worker (skipWaiting) y recarga la página.
 *
 * Aquí vive la lógica pura: el estado de la comprobación manual (Ajustes),
 * el «Más tarde» de esta sesión y la comprobación forzada con
 * `registration.update()`, con sus dependencias inyectadas para poder probarla.
 */

/** Estado de «Buscar actualizaciones» en Ajustes. */
export type EstadoComprobacion =
  | 'inactivo'
  | 'buscando'
  | 'al-dia'
  | 'hay-nueva'
  | 'error'
  | 'aplicando';

export type AccionComprobacion =
  | { tipo: 'buscar' }
  | { tipo: 'resultado'; hayNueva: boolean }
  | { tipo: 'fallo' }
  | { tipo: 'aplicar' };

export function reducirComprobacion(
  estado: EstadoComprobacion,
  accion: AccionComprobacion,
): EstadoComprobacion {
  switch (accion.tipo) {
    case 'buscar':
      // Mientras se aplica no se vuelve atrás (la página va a recargar).
      return estado === 'aplicando' ? estado : 'buscando';
    case 'resultado':
      if (estado !== 'buscando') {
        return estado;
      }
      return accion.hayNueva ? 'hay-nueva' : 'al-dia';
    case 'fallo':
      return estado === 'buscando' ? 'error' : estado;
    case 'aplicar':
      return 'aplicando';
    default:
      return estado;
  }
}

/** Almacén de la sesión (sessionStorage en la app; un objeto en los tests). */
export type AlmacenSesion = {
  getItem: (clave: string) => string | null;
  setItem: (clave: string, valor: string) => void;
};

export const CLAVE_MAS_TARDE = 'rumbo:actualizacion:mas-tarde';

// Respaldo en memoria por si el almacén falla (modo privado, bloqueado…).
let masTardeEnMemoria = false;

function almacenSesion(): AlmacenSesion | null {
  try {
    return typeof window !== 'undefined' ? window.sessionStorage : null;
  } catch {
    return null;
  }
}

/** ¿Ha pulsado «Más tarde» en esta sesión? */
export function aplazadaEnSesion(
  almacen: AlmacenSesion | null = almacenSesion(),
): boolean {
  if (masTardeEnMemoria) {
    return true;
  }
  try {
    return almacen?.getItem(CLAVE_MAS_TARDE) === '1';
  } catch {
    return false;
  }
}

/** Apunta el «Más tarde»: la ventana no vuelve a salir en esta sesión. */
export function aplazarEnSesion(
  almacen: AlmacenSesion | null = almacenSesion(),
) {
  masTardeEnMemoria = true;
  try {
    almacen?.setItem(CLAVE_MAS_TARDE, '1');
  } catch {
    // sin almacenamiento: basta con la memoria
  }
}

/** Solo para tests: olvida el «Más tarde» en memoria. */
export function _reiniciarAplazamiento() {
  masTardeEnMemoria = false;
}

/** Lo mínimo que se usa de un ServiceWorkerRegistration. */
export type RegistroSW = {
  update: () => Promise<unknown>;
  installing: { state: string } | null;
  waiting: unknown;
};

export type EntornoComprobacion = {
  /** `global.Actual.isUpdateReadyForDownload`. */
  lista: () => boolean;
  /** Registro del service worker (null si no hay, p. ej. en desarrollo). */
  registro: () => Promise<RegistroSW | null>;
  esperar: (ms: number) => Promise<void>;
  /** Tiempo máximo esperando a que se instale el worker nuevo. */
  tope?: number;
};

/**
 * Fuerza la comprobación: pide al navegador que mire si hay worker nuevo y,
 * si lo está instalando, espera (con tope) a que termine. Devuelve si hay una
 * versión lista para aplicar. Lanza si `update()` falla (sin red…).
 */
export async function comprobarActualizacion({
  lista,
  registro,
  esperar,
  tope = 20_000,
}: EntornoComprobacion): Promise<boolean> {
  if (lista()) {
    return true;
  }
  const reg = await registro();
  if (!reg) {
    return false;
  }
  await reg.update();
  const PASO = 250;
  for (let t = 0; t < tope; t += PASO) {
    if (lista() || reg.waiting) {
      return true;
    }
    if (!reg.installing || reg.installing.state === 'redundant') {
      break;
    }
    await esperar(PASO);
  }
  return lista() || Boolean(reg.waiting);
}

/** Comprobación real contra el service worker del navegador. */
export function buscarActualizacion(): Promise<boolean> {
  return comprobarActualizacion({
    lista: () => global.Actual?.isUpdateReadyForDownload?.() ?? false,
    registro: async () => {
      const sw = typeof navigator !== 'undefined' && navigator.serviceWorker;
      if (!sw) {
        return null;
      }
      return (
        (await sw.getRegistration(import.meta.env.BASE_URL)) ??
        (await sw.getRegistration()) ??
        null
      );
    },
    esperar: ms => new Promise(resolve => setTimeout(resolve, ms)),
  });
}

/** Versión legible: «v26.10.0 · a1b2c3d» (el commit solo si se conoce). */
export function versionLegible(
  version: string | null | undefined,
  commit: string | null | undefined,
): string {
  const partes: string[] = [];
  if (version) {
    partes.push(version.startsWith('v') ? version : `v${version}`);
  }
  if (commit) {
    partes.push(commit.slice(0, 7));
  }
  return partes.join(' · ');
}
